import { ApiError } from '@/utils/apiClient';

/**
 * The copy for a chat send a rate limit turned away (HTTP 429), so the voter
 * learns when they can send again instead of reading a generic failure. The
 * mobile app's `services/chatLimit.ts` is the same logic.
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Seconds until *retryAfter* (delta-seconds or an HTTP date) lifts, or `null` when unreadable. */
export function retryAfterSeconds(retryAfter: string | null, now = Date.now()): number | null {
  if (!retryAfter) return null;
  const seconds = Number(retryAfter);
  if (!Number.isNaN(seconds)) return Math.max(seconds, 0);
  const date = Date.parse(retryAfter);
  return Number.isNaN(date) ? null : Math.max((date - now) / 1000, 0);
}

/**
 * *seconds* as a rounded-up duration in *locale* ("1 minute", "3 hours"), or
 * `null` when this engine can't format units in that locale — the caller then
 * shows the variant without a time rather than one in the wrong language.
 */
export function formatWait(seconds: number, locale: string): string | null {
  const [value, unit] =
    seconds < 3600
      ? [Math.max(1, Math.ceil(seconds / 60)), 'minute']
      : [Math.ceil(seconds / 3600), 'hour'];
  try {
    if (Intl.NumberFormat.supportedLocalesOf(locale).length === 0) return null;
    const text = new Intl.NumberFormat(locale, {
      style: 'unit',
      unit,
      unitDisplay: 'long',
    }).format(value);
    // An engine without unit support prints the bare number.
    return text === new Intl.NumberFormat(locale).format(value) ? null : text;
  } catch {
    return null;
  }
}

/** The message for a send a rate limit turned away, or `null` for any other failure. */
export function chatLimitMessage(
  error: unknown,
  t: Translate,
  locale: string,
  now = Date.now()
): string | null {
  if (!(error instanceof ApiError) || error.status !== 429) return null;
  const seconds = retryAfterSeconds(error.retryAfter, now);
  const wait = seconds === null ? null : formatWait(seconds, locale);
  return wait ? t('errors.chatLimitReached', { wait }) : t('errors.chatLimitReachedNoTime');
}
